/**
 * Browser (OS-level) notifications for London Smoke & Cure — Admin role only.
 * Product scope: new orders, stock injections, production stocktakes, Njord sensor threshold alerts.
 *
 * Security: `requestAdminDesktopPermission` and `showAdminDesktopNotificationIfAllowed` take Firestore-shaped
 * `userData` (must include `role`) and re-check **`userHasAdminOrProductionRole`** internally — never trust a boolean from callers.
 * (**`AdminNewOrderDesktopNotifier`** still subscribes only for **Admin** — new-order alerts are not broadened to Production.)
 * Customers must never get permission prompts or internal desktop alerts (even with stale opt-in in localStorage).
 *
 * Optional per-user **notification hours** (`desktopNotifyHoursEnabled` + minute fields on `users/{uid}`):
 * enforced in `showAdminDesktopNotificationIfAllowed` via `isNowWithinDesktopNotifyHours`.
 */

import { isNowWithinDesktopNotifyHours } from '../utils/adminDesktopNotifyWindow';

export { isNowWithinDesktopNotifyHours };

const STORAGE_OPT_IN = 'lsc_admin_desktop_notifications_opt_in';

/** @param {Record<string, unknown> | null | undefined} userData */
export function parseRolesFromUserData(userData) {
    if (!userData?.role) return [];
    const role = userData.role;
    if (typeof role === 'string') {
        return role
            .split(',')
            .map((r) => r.trim())
            .filter((r) => r.length > 0);
    }
    if (Array.isArray(role)) {
        return role.map((r) => String(r).trim()).filter(Boolean);
    }
    return [String(role)];
}

/** @param {Record<string, unknown> | null | undefined} userData */
export function userHasAdminRole(userData) {
    return parseRolesFromUserData(userData).includes('Admin');
}

/** Production team — same desktop notification stack as Admin for ops milestones (with opt-in). */
export function userHasProductionRole(userData) {
    return parseRolesFromUserData(userData).includes('Production');
}

/** Admin or Production: eligible for internal ops / milestone desktop alerts (subject to opt-in + permission). */
export function userHasAdminOrProductionRole(userData) {
    return userHasAdminRole(userData) || userHasProductionRole(userData);
}

export function isBrowserNotificationSupported() {
    return typeof window !== 'undefined' && 'Notification' in window;
}

export function getDesktopOptIn() {
    try {
        return localStorage.getItem(STORAGE_OPT_IN) === '1';
    } catch {
        return false;
    }
}

export function setDesktopOptIn(enabled) {
    try {
        if (enabled) localStorage.setItem(STORAGE_OPT_IN, '1');
        else localStorage.removeItem(STORAGE_OPT_IN);
    } catch {
        /* ignore */
    }
}

/**
 * Request notification permission. Verifies Admin from `userData.role` (same source as Firestore `users/{uid}`).
 * @param {Record<string, unknown> | null | undefined} userData
 * @returns {Promise<NotificationPermission | 'unsupported'>}
 */
export async function requestAdminDesktopPermission(userData) {
    if (!userHasAdminOrProductionRole(userData) || !isBrowserNotificationSupported()) {
        return 'unsupported';
    }
    const current = Notification.permission;
    if (current === 'granted') {
        setDesktopOptIn(true);
        return 'granted';
    }
    if (current === 'denied') {
        return 'denied';
    }
    const result = await Notification.requestPermission();
    if (result === 'granted') {
        setDesktopOptIn(true);
    }
    return result;
}

/**
 * Show a desktop notification only if `userData` is Admin (re-checked here), has opted in, and permission granted.
 * @param {Record<string, unknown> | null | undefined} userData — profile with `role` (e.g. from Firestore)
 * @param {string} title
 * @param {string} body
 * @param {{ tag?: string, icon?: string, onClickNavigate?: string, onClick?: () => void }} [options]
 * @returns {boolean} whether a notification was shown
 */
export function showAdminDesktopNotificationIfAllowed(userData, title, body, options = {}) {
    if (!userHasAdminOrProductionRole(userData) || !isBrowserNotificationSupported()) return false;
    if (!getDesktopOptIn()) return false;
    if (Notification.permission !== 'granted') return false;
    if (!isNowWithinDesktopNotifyHours(userData)) return false;

    try {
        const icon = options.icon || `${window.location.origin}/logo192.png`;
        const n = new Notification(title, {
            body: body || '',
            icon,
            tag: options.tag || undefined,
            silent: false,
        });
        n.onclick = () => {
            try {
                window.focus();
                if (typeof options.onClick === 'function') {
                    options.onClick();
                } else if (options.onClickNavigate) {
                    window.location.assign(options.onClickNavigate);
                }
            } finally {
                n.close();
            }
        };
        return true;
    } catch {
        return false;
    }
}

const adminDesktopNotificationService = {
    parseRolesFromUserData,
    userHasAdminRole,
    userHasProductionRole,
    userHasAdminOrProductionRole,
    isBrowserNotificationSupported,
    getDesktopOptIn,
    setDesktopOptIn,
    requestAdminDesktopPermission,
    showAdminDesktopNotificationIfAllowed,
};

export default adminDesktopNotificationService;
